import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  MapPin,
  Plus,
  X,
  Navigation,
  Sparkles,
  Save,
  Loader2,
  Clock,
  Route as RouteIcon,
} from 'lucide-react'

interface Waypoint {
  id: string
  name: string
  address: string
  latitude: number
  longitude: number
}

interface RouteResult {
  distance: number
  duration: number
  geometry: [number, number][]
}

export function RoutePlanner() {
  const [waypoints, setWaypoints] = useState<Waypoint[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [routeResult, setRouteResult] = useState<RouteResult | null>(null)
  const [costing, setCosting] = useState<'auto' | 'truck'>('auto')
  const [isOptimized, setIsOptimized] = useState(false)

  // Geocode mutation
  const geocodeMutation = useMutation({
    mutationFn: async (query: string) => {
      const res = await fetch(`/api/routing/geocode?q=${encodeURIComponent(query)}`)
      if (!res.ok) throw new Error('Geocoding failed')
      return res.json() as Promise<{ results: any[] }>
    },
    onSuccess: (data) => {
      if (data.results.length > 0) {
        const result = data.results[0]
        addWaypoint({
          id: `wp_${Date.now()}`,
          name: result.label.split(',')[0],
          address: result.label,
          latitude: result.latitude,
          longitude: result.longitude,
        })
        setSearchQuery('')
      }
    },
  })

  // Calculate route mutation
  const calculateMutation = useMutation({
    mutationFn: async (optimize: boolean) => {
      const endpoint = optimize ? '/api/routing/optimize' : '/api/routing/calculate'
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          waypoints: waypoints.map(wp => ({
            latitude: wp.latitude,
            longitude: wp.longitude,
            name: wp.name
          })),
          costing,
        }),
      })
      if (!res.ok) throw new Error('Route calculation failed')
      return res.json() as Promise<{ route: RouteResult }>
    },
    onSuccess: (data, optimize) => {
      setRouteResult(data.route)
      setIsOptimized(optimize)
    },
  })

  // Save route mutation
  const saveMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await fetch('/api/routing/routes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          waypoints: waypoints.map((wp, i) => ({
            ...wp,
            waypoint_type: i === 0 ? 'start' : i === waypoints.length - 1 ? 'end' : 'stop'
          })),
          costing_type: costing,
          is_optimized: isOptimized,
          total_distance: routeResult?.distance,
          total_duration: routeResult?.duration,
          geometry: routeResult?.geometry,
        }),
      })
      if (!res.ok) throw new Error('Failed to save route')
      return res.json()
    },
  })

  const addWaypoint = (waypoint: Waypoint) => {
    setWaypoints([...waypoints, waypoint])
  }

  const removeWaypoint = (id: string) => {
    setWaypoints(waypoints.filter(wp => wp.id !== id))
    setRouteResult(null)
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      geocodeMutation.mutate(searchQuery)
    }
  }

  const formatDistance = (meters: number) => {
    const miles = meters / 1609.34
    return miles < 1 ? `${Math.round(meters)} m` : `${miles.toFixed(1)} mi`
  }

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`
  }

  const handleSaveRoute = () => {
    const name = prompt('Enter route name:')
    if (name) {
      saveMutation.mutate(name)
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Left: Waypoint Manager */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Waypoints
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex gap-2">
            <Input
              placeholder="Search address or place..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={geocodeMutation.isPending}
            />
            <Button
              type="submit"
              size="icon"
              disabled={!searchQuery.trim() || geocodeMutation.isPending}
            >
              {geocodeMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </Button>
          </form>

          {/* Waypoint List */}
          {waypoints.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MapPin className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p>Add waypoints to start planning</p>
              <p className="text-sm mt-1">Search for addresses above</p>
            </div>
          ) : (
            <div className="space-y-2">
              {waypoints.map((wp, index) => (
                <div
                  key={wp.id}
                  className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{wp.name}</p>
                    <p className="text-sm text-muted-foreground truncate">{wp.address}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 flex-shrink-0"
                    onClick={() => removeWaypoint(wp.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Settings */}
          {waypoints.length > 0 && (
            <div className="pt-4 border-t space-y-3">
              <div>
                <Label>Vehicle Type</Label>
                <Select value={costing} onValueChange={(v: any) => setCosting(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto (Car/Van)</SelectItem>
                    <SelectItem value="truck">Truck</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2">
                <Button
                  className="flex-1 gap-2"
                  onClick={() => calculateMutation.mutate(false)}
                  disabled={waypoints.length < 2 || calculateMutation.isPending}
                >
                  {calculateMutation.isPending && !isOptimized ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Navigation className="h-4 w-4" />
                  )}
                  Calculate
                </Button>
                {waypoints.length >= 3 && (
                  <Button
                    className="flex-1 gap-2"
                    variant="secondary"
                    onClick={() => calculateMutation.mutate(true)}
                    disabled={calculateMutation.isPending}
                  >
                    {calculateMutation.isPending && isOptimized ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                    Optimize
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Right: Route Results */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RouteIcon className="h-5 w-5" />
            Route Results
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!routeResult ? (
            <div className="text-center py-12 text-muted-foreground">
              <RouteIcon className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p>Calculate a route to see results</p>
              <p className="text-sm mt-1">Add 2+ waypoints and click Calculate</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <RouteIcon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Distance</p>
                        <p className="text-2xl font-bold">{formatDistance(routeResult.distance)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Clock className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Duration</p>
                        <p className="text-2xl font-bold">{formatDuration(routeResult.duration)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">
                  {waypoints.length} waypoints
                </Badge>
                <Badge variant="outline">
                  {costing === 'truck' ? 'Truck' : 'Auto'} routing
                </Badge>
                {isOptimized && (
                  <Badge className="bg-purple-500">
                    <Sparkles className="h-3 w-3 mr-1" />
                    Optimized
                  </Badge>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  className="flex-1 gap-2"
                  onClick={handleSaveRoute}
                  disabled={saveMutation.isPending}
                >
                  {saveMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Save Route
                </Button>
              </div>

              {saveMutation.isSuccess && (
                <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-green-700 dark:text-green-400 text-sm">
                  ✓ Route saved successfully!
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
